import React, { useEffect, useState } from "react";
import { PageHeader, DataTable, StatusBadge } from "@/components/common";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

const empty = { name: "", city: "", district: "", contact_name: "", contact_email: "", contact_phone: "", status: "active" };
const fmt = new Intl.NumberFormat("tr-TR");

export default function MunicipalitiesPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const isSuper = user?.role === "super_admin";

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/municipalities");
      setRows(data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function openNew() {
    setEditing(null); setForm(empty); setOpen(true);
  }
  function openEdit(r) {
    setEditing(r);
    setForm({
      name: r.name, city: r.city, district: r.district,
      contact_name: r.contact_name || "", contact_email: r.contact_email || "",
      contact_phone: r.contact_phone || "", status: r.status || "active",
    });
    setOpen(true);
  }

  async function save() {
    try {
      if (editing) {
        await api.put(`/admin/municipalities/${editing.id}`, form);
        toast.success("Belediye güncellendi");
      } else {
        await api.post("/admin/municipalities", form);
        toast.success("Belediye eklendi");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function remove(r) {
    if (!window.confirm(`"${r.name}" belediyesini silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/admin/municipalities/${r.id}`);
      toast.success("Silindi"); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        overline="Yönetim"
        title="Belediyeler"
        description="Sisteme kayıtlı belediyeler ve kısa istatistikleri."
        actions={isSuper && (
          <Button className="bg-red-600 hover:bg-red-700 rounded-sm h-10 font-heading font-semibold uppercase text-xs tracking-wider"
            onClick={openNew} data-testid="add-municipality-button">
            <Plus className="w-4 h-4 mr-1.5" /> Yeni Belediye
          </Button>
        )}
      />

      {loading ? <div className="text-sm text-slate-500">Yükleniyor…</div> : (
        <DataTable
          testid="municipalities-table"
          columns={[
            { label: "Belediye", render: (r) => <div><div className="font-medium">{r.name}</div><div className="text-xs text-slate-500">{r.district} · {r.city}</div></div> },
            { label: "Yetkili", render: (r) => (<div className="text-xs"><div>{r.contact_name || "—"}</div><div className="text-slate-500">{r.contact_email || ""}</div></div>) },
            { label: "Site", render: (r) => fmt.format(r.site_count || 0) },
            { label: "Aktif Kampanya", render: (r) => fmt.format(r.campaign_count || 0) },
            { label: "Durum", render: (r) => <StatusBadge status={r.status} /> },
            {
              label: "İşlemler", render: (r) => (
                <div className="flex items-center gap-2">
                  {isSuper && <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-mun-${r.id}`}><Pencil className="w-4 h-4" /></Button>}
                  {isSuper && <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => remove(r)} data-testid={`delete-mun-${r.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </div>
              )
            },
          ]}
          rows={rows}
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Belediye Düzenle" : "Yeni Belediye Ekle"}</DialogTitle>
            <DialogDescription>Belediye bilgilerini ve yetkili iletişim detaylarını yönetin. Belediyeler; yayıncı siteler, kampanyalar ve reklam alanlarının ana kırılım birimidir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Belediye Adı</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="muni-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>İl</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="muni-form-city" />
              </div>
              <div>
                <Label>İlçe</Label>
                <Input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} data-testid="muni-form-district" />
              </div>
            </div>
            <div>
              <Label>Yetkili Kişi</Label>
              <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>E-posta</Label>
                <Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
              </div>
              <div>
                <Label>Telefon</Label>
                <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Durum</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktif</SelectItem>
                  <SelectItem value="inactive">Pasif</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} className="bg-red-600 hover:bg-red-700 rounded-sm" data-testid="muni-form-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
